import { useState, useCallback, useEffect } from "react";
import { getBoards, createBoard, saveToBoard } from "../APIs/saveApi";
import { trackEvent } from "../utils/analytics";

// ── Module-level bookmark cache ───────────────────────────────────────────────
// Initialised once when the module first loads (reads localStorage).
// Lives for the entire browser session — survives component remounts.
// localStorage keeps it across full page reloads.

const _saved = new Set(
  (() => {
    try { return JSON.parse(localStorage.getItem("saved_posts") || "[]"); }
    catch { return []; }
  })()
);

const isSaved  = (id) => _saved.has(String(id));
const markSaved = (id) => {
  _saved.add(String(id));
  try { localStorage.setItem("saved_posts", JSON.stringify([..._saved])); } catch {}
};
// ─────────────────────────────────────────────────────────────────────────────

const useSave = (postId, isBookmarked = false) => {
  const [open, setOpen]             = useState(false);
  const [boards, setBoards]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [savedBoardId, setSavedBoardId] = useState(null);
  const [newBoardName, setNewBoardName] = useState("");
  const [isPrivate, setIsPrivate]   = useState(false);
  const [creating, setCreating]     = useState(false);
  const [error, setError]           = useState(null);

  // Check module cache first — reliable across remounts
  const [saved, setSaved] = useState(() => isSaved(postId) || isBookmarked);

  // If the API says this post is bookmarked, lock it in the cache too
  useEffect(() => {
    if (isBookmarked && !isSaved(postId)) {
      markSaved(postId);
      setSaved(true);
    }
  }, [isBookmarked, postId]);

  const openModal = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const data = await getBoards();
      setBoards(data);
    } catch {
      setError("Could not load boards.");
    } finally {
      setLoading(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    setError(null);
    setNewBoardName("");
    setIsPrivate(false);
  }, []);

  const handleSaveToBoard = useCallback(
    async (boardId) => {
      if (saving) return;
      setSaving(true);
      try {
        await saveToBoard(boardId, postId);
        setSavedBoardId(boardId);
        markSaved(postId);   // update module cache + localStorage
        setSaved(true);
        trackEvent("save_action", { postId });
        setTimeout(() => closeModal(), 600);
      } catch {
        setError("Could not save post.");
      } finally {
        setSaving(false);
      }
    },
    [postId, saving, closeModal]
  );

  const handleCreateBoard = useCallback(async () => {
    if (!newBoardName.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const newBoard = await createBoard(newBoardName.trim(), isPrivate);
      setBoards((prev) => [...prev, newBoard]);
      setNewBoardName("");
      setIsPrivate(false);
    } catch {
      setError("Could not create board.");
    } finally {
      setCreating(false);
    }
  }, [newBoardName, isPrivate, creating]);

  return {
    open, boards, loading, saving, creating,
    savedBoardId, newBoardName, isPrivate, error,
    saved,
    openModal, closeModal, handleSaveToBoard,
    handleCreateBoard, setNewBoardName, setIsPrivate,
  };
};

export default useSave;
